import { Component, inject, signal, computed, ViewChild, ElementRef, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { Router } from '@angular/router';
import { Location, CommonModule } from '@angular/common';
import { Subscription } from 'rxjs';
import { ApiService } from '../../../../core/services/api.service';
import { IconComponentV2 } from '../../../../shared/components/icon-component/icon.component';
import { ButtonComponent } from '../../../../shared/components/ui/button/button.component';
import { ChatbotMessageListComponent } from '../../../public/chatbot/chat-interface/components/chatbot-message-list/chatbot-message-list.component';
import { CallTranscriptionComponent, TranscriptionMessage } from '../call-transcription/call-transcription.component';
import { DatePipe } from '@angular/common';
import { ToggleBtnsComponent, toggleHeader } from '../../../../shared/components/toggle-btns/toggle-btns.component';
import { ThemeService } from '../../../../core/services/theme.service';
import { ProductConfigService } from '../../../../core/services/product-config.service';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { TemplateRef, DestroyRef } from '@angular/core';
import { CommonService } from '../../../../core/services/common.service';
import { BreakpointObserver } from '@angular/cdk/layout';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { environment } from '../../../../../environments/environment';

interface infoData {
  id: string;
  name: string;
  email: string;
  phoneNumber: string;
}

interface callDetailsData {
  call: any;
  customerDetails: infoData;
  agentDetails: infoData;
}

@Component({
  selector: 'app-call-details',
  standalone: true,
  imports: [
    CommonModule,
    IconComponentV2,
    CallTranscriptionComponent,
    ToggleBtnsComponent,
    MatDialogModule
  ],
  providers: [DatePipe],
  templateUrl: './call-details.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: []
})
export class CallDetailsComponent implements OnInit, OnDestroy {

  private _router = inject(Router);
  private _location = inject(Location);
  public _data: callDetailsData = {} as callDetailsData;
  private _apiService = inject(ApiService);
  private _themeService = inject(ThemeService);
  private _productConfigService = inject(ProductConfigService);
  private _datePipe = inject(DatePipe);
  private _dialog = inject(MatDialog);
  public _commonService = inject(CommonService);
  private _breakpointObserver = inject(BreakpointObserver);
  private _destroyRef = inject(DestroyRef);
  private _fetchSubscription?: Subscription;

  productConfig = computed(() => {
    return this._productConfigService.getProductConfig(this._data.call?.productName);
  });

  isSmallScreen = signal(false);

  constructor() {
    const navigation = this._router.getCurrentNavigation();
    if (navigation?.extras.state && navigation.extras.state['callData']) {
      const callData = navigation.extras.state['callData'];
      this._data = {
        call: callData,
        customerDetails: callData.customerDetails || {},
        agentDetails: callData.agentDetails || {}
      };
    }

    this._breakpointObserver.observe(['(max-width: 1366px)']).pipe(takeUntilDestroyed(this._destroyRef)).subscribe((result) => {
      this.isSmallScreen.set(result.matches);
    });

  }

  isLoading = signal(false);
  activeTab = signal<string>('GOOGLE_MEET');
  
  tabToggleOptions = computed<toggleHeader>(() => {
    const isDark = this._themeService.theme() === 'dark';
    return {
      showToggleButton: true,
      showlabels: true,
      showIcon: true,
      size: 'md',
      activeBgColor: isDark ? '#141437' : '#FFFFFF',
      activeTextColor: isDark ? '#FFFFFF' : '#0F172A',
      inactiveTextColor: isDark ? '#d4d4d8' : '#64748B',
      toggleOptions: [
        {
          label: 'Google Meet',
          value: 'GOOGLE_MEET',
          iconName: 'video',
          isTable: false,
          tooltip: 'Google Meet Recordings'
        },
        {
          label: 'AI Assistant',
          value: 'AI_ASSISTANT',
          iconName: 'sparkles',
          isTable: true,
          tooltip: 'Interaction with AI Assistant'
        }
      ]
    };
  });

  messages = signal<any[]>([]);

  // Pagination & History
  historyPage = 1;
  isLoadingHistory = signal(false);
  hasMoreHistory = signal(true);
  readonly HISTORY_LIMIT = 10;
  openSessionIndex = signal<number | null>(null);
  selectedRecording = signal<any | null>(null);

  @ViewChild('scrollArea') scrollArea!: ElementRef;
  @ViewChild('audioPlayer') audioPlayer!: ElementRef<HTMLAudioElement>;
  @ViewChild('audioRecording') audioRecording!: TemplateRef<any>;
  @ViewChild('transcriptContainer') transcriptContainer!: ElementRef;
  
  // Audio Player Signals
  activePlayingCall = signal<any>(null);
  currentSegmentIndex = signal<number>(0);
  isPlaying = signal<boolean>(false);
  isMuted = signal<boolean>(false);
  currentTime = signal<number>(0);
  audioDuration = signal<number>(0);
  activeTranscriptIndex = signal(-1);
  isAutoScrolling = signal(true);
  scrollDirection = signal<'up' | 'down'>('down');
  isFinite = isFinite;

  private audio = new Audio();
  private durationSub: Subscription | null = null;
  private _isProgrammaticScroll = false;
  private _isChangingSource = false;
  private _dialogRef: any = null;

  chatMessages = computed(() => {
    if (this.activeTab() !== 'AI_ASSISTANT') return [];
    const data = this.messages();
    if (!data) return [];
    
    // Handle both single object and array of objects
    const interactions = Array.isArray(data) ? data : [data];
    return this.parseHistoryItems(interactions, 'AI_ASSISTANT');
  });

  googleMeetSessions = computed(() => {
    if (this.activeTab() !== 'GOOGLE_MEET') return [];
    const data = this.messages();
    if (!data) return [];
    
    const sessions = Array.isArray(data) ? data : [data];
    return sessions.map((s: any) => {
      const messages = this.parseHistoryItems(s.transcriptText || [], 'GOOGLE_MEET');
      
      // Extract unique speakers (agents) from messages
      let agents = Array.from(new Set(
        messages
          .filter(m => m.role === 'AGENT')
          .map(m => m.sender)
      )).join(', ');

      // Use agentDetails from call metadata as fallback/primary source if available
      if (!agents && this._data.agentDetails?.name) {
        agents = this._data.agentDetails.name;
      }

      // Calculate duration from messages timestamps
      const audioUrlsRaw = s.audioUrls || [];
      const validAudioUrls = Array.isArray(audioUrlsRaw) ? audioUrlsRaw.filter((a: any) => a.recordingStartTime) : [];
      const sortedAudioUrls = validAudioUrls.sort((a: any, b: any) => new Date(a.recordingStartTime).getTime() - new Date(b.recordingStartTime).getTime());
      
      let baseTsSec = Number.MAX_SAFE_INTEGER;
      const MAX_SILENCE_GAP_SEC = 30 * 60; // 30 minutes — beyond this, audio start is considered bad data

      if (sortedAudioUrls.length > 0) {
        baseTsSec = new Date(sortedAudioUrls[0].recordingStartTime).getTime() / 1000;
      }

      if (messages.length > 0 && messages[0].timestamp) {
        const firstTranscriptSec = messages[0].timestamp / 1000;
        // If the audio start is unreasonably early vs first speech (bad recordingStartTime like midnight),
        // anchor to the first transcript instead so the timeline shows the real call duration.
        if (baseTsSec === Number.MAX_SAFE_INTEGER || (firstTranscriptSec - baseTsSec) > MAX_SILENCE_GAP_SEC) {
          baseTsSec = firstTranscriptSec;
        }
      }

      // Fallback if neither audio nor transcript had usable timestamps
      if (baseTsSec === Number.MAX_SAFE_INTEGER) {
        if (s.recordingStartTime) {
          baseTsSec = typeof s.recordingStartTime === 'number' ? s.recordingStartTime / 1000 : new Date(s.recordingStartTime).getTime() / 1000;
        } else {
          baseTsSec = 0;
        }
      }

      const audioSegments = sortedAudioUrls.map((a: any) => {
           const startMs = new Date(a.recordingStartTime).getTime();
           const startTimeInCall = Math.max(0, (startMs / 1000) - baseTsSec);
           return {
               url: a.url || a.signedUrl,
               duration: a.duration,
               recordingStartTime: startMs,
               startTimeInCall: startTimeInCall,
               endTimeInCall: startTimeInCall + a.duration
           };
      });

      // Duration: use the end of the last audio segment, BUT cap it at the last transcript + small buffer.
      // This prevents late-rejoining bots (with no speech) from inflating the timeline by hours.
      let durationSeconds = 0;
      if (audioSegments.length > 0) {
        const rawDuration = audioSegments[audioSegments.length - 1].endTimeInCall;
        
        if (messages.length > 0) {
          const lastTranscriptOffset = (messages[messages.length - 1].timestamp - baseTsSec * 1000) / 1000;
          // Find the audio segment that contains (or follows) the last transcript word
          const lastActiveSegment = audioSegments
            .slice()
            .reverse()
            .find((seg: any) => seg.startTimeInCall <= lastTranscriptOffset);
          
          if (lastActiveSegment) {
            // Cap to end of the segment that contains the last spoken word
            durationSeconds = lastActiveSegment.endTimeInCall;
          } else {
            durationSeconds = rawDuration;
          }
        } else {
          durationSeconds = rawDuration;
        }
      } else {
        const apiDuration = s.audioUrls?.[0]?.duration;
        durationSeconds = typeof apiDuration === 'number' && apiDuration > 0 ? apiDuration : 0;
  
        // Fallback to messages timestamps if API duration is missing
        if (durationSeconds === 0 && messages.length > 1) {
          const recordingStartTime = s.audioUrls?.[0]?.recordingStartTime;
          const baseTs = (recordingStartTime) 
            ? (typeof recordingStartTime === 'number' ? recordingStartTime : new Date(recordingStartTime).getTime()) 
            : messages[0].timestamp;
          const lastTs = messages[messages.length - 1].timestamp;
          
          if (baseTs && lastTs) {
            durationSeconds = Math.floor((lastTs - baseTs) / 1000);
          }
        }
      }

      const mins = Math.floor(durationSeconds / 60);
      const secs = Math.floor(durationSeconds % 60);
      const durationStr = `${mins}:${secs.toString().padStart(2, '0')}`;

      // Filter messages to only those whose offset falls within the audio timeline.
      // Messages from other bot sessions hours/days later (with no matching audio) are dropped.
      const syncedMessages = durationSeconds > 0
        ? messages.filter(m => {
            const offsetSec = (m.timestamp - baseTsSec * 1000) / 1000;
            return offsetSec >= 0 && offsetSec <= durationSeconds + 30; // +30s buffer for last message
          })
        : messages;

      return {
        ...s,
        meetLink: s.meetLink,
        createdAt: s.createdAt,
        messages: syncedMessages,
        audioSegments,
        baseTimestamp: baseTsSec * 1000,
        agents: agents || 'No Agents',
        duration: durationSeconds,
        formattedDuration: durationStr
      };
    });
  });

  private parseHistoryItems(items: any, type: string): any[] {
    if (!items) return [];
    const itemsArray = Array.isArray(items) ? items : [items];
    const messages: any[] = [];
    if (type === 'GOOGLE_MEET') {
      itemsArray.forEach(item => {
        // Add User message
        if (item.role == 'client') {
          messages.push({
            role: 'CUSTOMER',
            content: item.text,
            sender: item.speaker || 'Customer',
            time: item.time_stamp ? new Date(item.time_stamp * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '',
            timestamp: item.time_stamp ? item.time_stamp * 1000 : null,
          });
        }

        // Add AI Assistant message
        if (item.role == 'llm') {
          messages.push({
            role: 'AI',
            content: item.text,
            sender: 'AI',
            time: item.timestamp ? new Date(item.timestamp * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '',
            timestamp: item.timestamp ? item.timestamp * 1000 : null,
          });
        }

        // Add Agent message
        if (['SalesAgent', 'salesAgent', 'agent'].includes(item.role)) {
          messages.push({
            role: 'AGENT',
            content: item.text,
            sender: Array.isArray(item.speaker) ? (item.speaker[0] || 'Agent') : (item.speaker || 'Agent'),
            time: item.time_stamp ? new Date(item.time_stamp * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '',
            timestamp: item.time_stamp ? item.time_stamp * 1000 : null,
          });
        }
      });
    } else {
      itemsArray.forEach(item => {
        // Add User message
        if (item.user_text) {
          const userTime = item.timestamp_query ? new Date(item.timestamp_query * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '';
          const teamName = this._data.call?.teamName && this._data.call.teamName !== 'N.A' ? this._data.call.teamName : 'L1';
          const senderName = this._data.agentDetails?.name ? `${this._data.agentDetails.name} (${teamName})` : 'Agent';
          
          messages.push({
            role: 'CUSTOMER',
            content: item.user_text,
            sender: senderName,
            time: userTime,
            formattedTime: userTime,
            timestamp: item.timestamp_query ? item.timestamp_query * 1000 : null,
          });
        }

        // Add Agent message
        if (item.agent_reply) {
          const botTime = item.timestamp_reply ? new Date(item.timestamp_reply * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '';
          messages.push({
            role: 'AI',
            content: item.agent_reply,
            sender: 'AI',
            time: botTime,
            formattedTime: botTime,
            timestamp: item.timestamp_reply ? item.timestamp_reply * 1000 : null,
          });
        }

      });
    }

    return messages.sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0));
  }

  conversation_type = {
    AI_ASSISTANT: 'AI_ASSISTANT',
    GOOGLE_MEET: 'GOOGLE_MEET',
  };

  getInitials(name: string): string {
    if (!name) return 'NA';
    return name
      .split(' ')
      .filter(n => n)
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  }

  selectRecording(recording: any): void {
    const isSameRecording = this.isSameRecording(recording, this.activePlayingCall());
    
    // Transitioning from mini-player to full view:
    // Close dialog WITHOUT wiping activePlayingCall state
    if (this._dialogRef) {
      this.closePlayer(true);
    }
    
    this.selectedRecording.set(recording);
    
    // Immediately format for transcripts and metadata display
    if (recording) {
      this.prepareRecordingData(recording);
      if (!isSameRecording) {
        this.currentTime.set(0);
      }
    }
  }

  private prepareRecordingData(recording: any): void {
    const messages = recording.messages || [];
    const audioUrlObj = recording.audioUrls?.[0];
    
    // Use baseTimestamp pre-calculated in googleMeetSessions to guarantee identical timeline anchors
    const baseTimestamp = recording.baseTimestamp !== undefined 
      ? recording.baseTimestamp 
      : (messages.length > 0 ? messages[0].timestamp : 0);
      
    const lastTimestamp = messages.length > 0 ? messages[messages.length - 1].timestamp : 0;

    // Priority: 1. Segments duration, 2. API duration, 3. Stored recording duration, 4. Estimated from transcript
    let estimatedDuration = recording.duration || 0;
    if (!estimatedDuration) {
      if (recording.audioSegments && recording.audioSegments.length > 0) {
        estimatedDuration = recording.audioSegments[recording.audioSegments.length - 1].endTimeInCall;
      } else {
        const apiDuration = audioUrlObj?.duration;
        estimatedDuration = typeof apiDuration === 'number' && apiDuration > 0
          ? apiDuration
          : (lastTimestamp - baseTimestamp) / 1000;
      }
    }

    this.activePlayingCall.set({
      ...recording,
      business: { name: this._data.call?.productName || 'Business' },
      callType: { 
        dateTime: this._datePipe.transform(recording.createdAt, 'MMM d, yyyy • hh:mm a') || '',
        platform: 'Google Meet'
      },
      estimatedDuration,
      formattedTranscript: messages.map((m: any) => ({
        ...m,
        text: m.content,
        sender: m.sender || (m.role === 'AGENT' ? 'Agent' : (m.role === 'AI' ? 'AI Assistant' : 'Customer')),
        formattedTime: this.formatTime((m.timestamp - baseTimestamp) / 1000),
        timestamp: m.timestamp - baseTimestamp
      })),
      audioUrl: recording.audioUrls?.[0]?.signedUrl || ''
    });

    // If we have a stored duration in the recording, use it
    if (recording.duration && typeof recording.duration === 'number') {
      this.audioDuration.set(recording.duration);
    }

    // Reset playback and auto-scroll state ONLY for new selection
    this.activeTranscriptIndex.set(-1);
    this.isAutoScrolling.set(true);
    this._isChangingSource = false;
  }

  backToList(): void {
    this.selectedRecording.set(null);
  }

  playRecording(recording: any, startTime: number = 0): void {
    const isSameCall = this.isSameRecording(recording, this.activePlayingCall());
    
    if (isSameCall && startTime === 0 && this.isPlaying()) {
      this.togglePlay();
      return;
    }

    if (!isSameCall) {
      this.prepareRecordingData(recording);
      this.isPlaying.set(false);
      this.currentTime.set(0);
    }

    const segments = recording.audioSegments || [];
    let segmentIndex = 0;
    let fileOffset = startTime;
    let actualStartTime = startTime;
    let signedUrl = recording.audioUrls?.[0]?.signedUrl;

    if (segments.length > 0) {
      // Find the segment that contains this time position.
      // Handles 3 cases: (a) within a segment, (b) in a gap between segments → snap to next segment start, (c) past the last segment
      segmentIndex = segments.findIndex((s: any) => startTime >= s.startTimeInCall && startTime < s.endTimeInCall);

      if (segmentIndex === -1) {
        // startTime is either in a gap or past the end. Find the next segment that starts after startTime.
        const nextIdx = segments.findIndex((s: any) => s.startTimeInCall > startTime);
        if (nextIdx !== -1) {
          // In a gap — snap forward to the start of the next segment
          segmentIndex = nextIdx;
          actualStartTime = segments[nextIdx].startTimeInCall;
          fileOffset = 0;
        } else {
          // Past all segments — clamp to start of last segment
          segmentIndex = segments.length - 1;
          actualStartTime = segments[segmentIndex].startTimeInCall;
          fileOffset = 0;
        }
      } else {
        fileOffset = startTime - segments[segmentIndex].startTimeInCall;
      }
      signedUrl = segments[segmentIndex].url;
    }

    if (signedUrl) {
      this._isChangingSource = true;
      
      // 1. Durations — always use the pre-computed capped duration from the session data
      const isGoogleMeet = recording.platform === 'GoogleMeet' || !!recording.meetLink;
      if (isGoogleMeet && (!this.audioDuration() || this.audioDuration() === 0)) {
        if (recording.duration && recording.duration > 0) {
          this.audioDuration.set(recording.duration);
        } else {
          this.fetchDuration(signedUrl, recording);
        }
      } else if (recording.duration && recording.duration > 0) {
        // Re-affirm the capped duration on every seek so it never drifts back to the raw segment total
        this.audioDuration.set(recording.duration);
      } else if (segments.length > 0) {
        this.audioDuration.set(segments[segments.length - 1].endTimeInCall);
      }

      // 2. Stream Setup (Ensure INT startTime)
      const flooredStart = Math.floor(fileOffset);
      const streamUrl = `${environment.apiUrl}/call-audit/stream-audio?url=${encodeURIComponent(signedUrl)}&productName=${encodeURIComponent(this._data.call?.productName || '')}&referer=${encodeURIComponent(recording.meetLink || '')}&startTime=${flooredStart}`;

      // 3. UI Offset Update
      const baseOffset = Math.floor(actualStartTime);
      this.currentSegmentIndex.set(segmentIndex);
      this.activePlayingCall.update(c => c ? ({ ...c, baseOffset, fileOffset }) : ({ ...recording, baseOffset, fileOffset }));
      this.currentTime.set(actualStartTime);

      // 4. Player UI Management - Only open if not already open and not just seeking
      const isSeeking = isSameCall && startTime > 0;
      if (!this._dialogRef && !this.selectedRecording()) {
        this._dialogRef = this._dialog.open(this.audioRecording, {
          width: this.isSmallScreen() ? '320px' : '420px',
          panelClass: 'audio-player-dialog',
          position: { bottom: this.isSmallScreen() ? '18px' : '40px', right: this.isSmallScreen() ? '28px' : '40px' },
          hasBackdrop: false
        });

        this._dialogRef.afterClosed().subscribe(() => {
          if (this._dialogRef) {
            this._dialogRef = null;
            this.closePlayer();
          }
        });
      }

      // 5. Immediate Transcript Sync for Visual Feedback
      if (isSeeking) {
        this.updateActiveTranscriptIndex(startTime);
      }

      // 6. Source Load and Play with explicit event handling
      this.audio.pause();
      this.audio.oncanplay = null;
      this.audio.src = streamUrl;
      this.audio.load();

      this.audio.oncanplay = () => {
        this.audio.oncanplay = null;
        this._isChangingSource = false;
        this.audio.play().then(() => {
          this.isPlaying.set(true);
        }).catch(err => {
          console.error('Audio playback error:', err);
          this.isPlaying.set(false);
        });
      };
    }
  }

  private isSameRecording(rec1: any, rec2: any): boolean {
    if (!rec1 || !rec2) return false;
    // Compare by ID if available, otherwise fallback to meetLink and date
    if (rec1.id && rec2.id) return rec1.id === rec2.id;
    return rec1.meetLink === rec2.meetLink && rec1.createdAt === rec2.createdAt;
  }

  private fetchDuration(url: string, recording: any): void {
    this.durationSub?.unsubscribe();
    const durationParams = {
      url,
      productName: (this._data.call?.productName || '').toLowerCase(),
      referer: recording.meetLink || ''
    };

    this.durationSub = this._apiService.get('/call-audit/audio-duration', durationParams).subscribe({
      next: (res: any) => {
        const duration = res?.data?.duration ?? res?.duration ?? 0;
        if (duration > 0) {
          this.audioDuration.set(duration);
          recording.duration = duration;
          this.activePlayingCall.update(c => c ? ({ ...c, duration }) : null);
        }
      },
      error: (err) => console.error('Error fetching duration:', err)
    });
  }

  onAudioEnded(): void {
    this.isPlaying.set(false);
    this.currentTime.set(this.audioDuration());
    this.activeTranscriptIndex.set(-1);
  }

  onTranscriptScroll(event: Event): void {
    if (this._isProgrammaticScroll || !this.activePlayingCall()) return;
    
    // Only detect manual scroll if the user has actually interacted or if audio is playing
    if (this.currentTime() > 0 || this.isPlaying()) {
      this.isAutoScrolling.set(false);
      this.updateScrollDirection();
    }
  }

  private updateScrollDirection(): void {
    const container = this.transcriptContainer?.nativeElement;
    if (!container || this.activeTranscriptIndex() === -1) return;

    const elements = container.querySelectorAll('.transcript-segment');
    const targetElement = elements[this.activeTranscriptIndex()] as HTMLElement;

    if (targetElement) {
      const containerRect = container.getBoundingClientRect();
      const elementRect = targetElement.getBoundingClientRect();
      
      // Compare the top of the element with the container top
      const isAbove = elementRect.top < containerRect.top;
      this.scrollDirection.set(isAbove ? 'up' : 'down');
    }
  }

  resumeAutoScroll(): void {
    this.isAutoScrolling.set(true);
    const index = this.activeTranscriptIndex();
    if (index !== -1) {
      this.scrollToActiveTranscript(index);
    }
  }

  private scrollToActiveTranscript(index: number): void {
    setTimeout(() => {
      const container = this.transcriptContainer?.nativeElement;
      if (!container) return;
      
      const elements = container.querySelectorAll('.transcript-segment');
      const targetElement = elements[index] as HTMLElement;
      
      if (targetElement) {
        this._isProgrammaticScroll = true;
        
        // Only scroll if not already visible or to center it
        const offset = targetElement.offsetTop - container.offsetTop - (container.clientHeight / 2) + (targetElement.clientHeight / 2);
        container.scrollTo({
          top: offset,
          behavior: 'smooth'
        });

        // Reset programmatic scroll flag after animation
        setTimeout(() => {
          this._isProgrammaticScroll = false;
        }, 800);
      }
    }, 0);
  }


  togglePlay(): void {
    if (this.isPlaying()) {
      this.audio.pause();
    } else {
      const recording = this.selectedRecording() || this.activePlayingCall();
      if (!recording) return;

      const duration = this.audioDuration();
      const current = this.currentTime();
      
      // Check if source is uninitialized (empty or current page URL)
      const isUninitialized = !this.audio.src || this.audio.src === '' || this.audio.src === window.location.href;
      // Check if audio has ended or is at the very end
      const isEnded = this.audio.ended || (duration > 0 && current >= duration - 0.1);

      if (isUninitialized || isEnded) {
        this.playRecording(recording, isEnded ? 0 : current);
        return;
      }

      this.audio.play().catch(err => {
        console.error('Error resuming audio playback:', err);
        this.isPlaying.set(false);
      });
    }
  }

  toggleMute(): void {
    const newMutedState = !this.isMuted();
    this.audio.muted = newMutedState;
    this.isMuted.set(newMutedState);
  }

  seek(event: MouseEvent): void {
    const el = event.currentTarget as HTMLElement;
    const rect = el.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const percentage = (x / rect.width);
    const targetTime = percentage * this.audioDuration();

    const recording = this.selectedRecording() || this.activePlayingCall();
    if (recording) {
      this.playRecording(recording, targetTime);
    }
  }

  seekFromPercentage(percentage: number): void {
    const duration = this.audioDuration();
    if (!duration || duration <= 0) return;

    const targetTime = (percentage / 100) * duration;
    const recording = this.selectedRecording() || this.activePlayingCall();

    if (recording && isFinite(targetTime)) {
      this.playRecording(recording, targetTime);
    }
  }

  closePlayer(preserveState: boolean = false): void {
    if (!preserveState) {
      this.audio.pause();
    }
    
    if (this._dialogRef) {
      this._dialogRef.close();
      this._dialogRef = null;
    }

    if (!preserveState) {
      this.activePlayingCall.set(null);
      this.isPlaying.set(false);
    }
  }

  formatTime(seconds: number): string {
    if (isNaN(seconds) || !isFinite(seconds)) return '0:00';
    if (seconds <= 0) return '0:00';
    
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    
    if (hrs > 0) {
      return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }

  ngOnInit(): void {
    this.setupAudioListeners();
    console.log('ngOnInit History state:', history.state);
    if (!this._data.call && history.state && history.state.callData) {
      const callData = history.state.callData;
      this._data = {
        call: callData,
        customerDetails: callData.customerDetails || {},
        agentDetails: callData.agentDetails || {}
      };
    }
    
    if (this._data && this._data.call) {
      this.fetchCallDetails();
    }
  }

  private setupAudioListeners(): void {
    this.audio.ontimeupdate = () => {
      const baseOffset = this.activePlayingCall()?.baseOffset || 0;
      const time = this.audio.currentTime + baseOffset;
      this.currentTime.set(time);

      this.updateActiveTranscriptIndex(time);
    };

    this.audio.onloadedmetadata = () => {
      const browserDuration = this.audio.duration;
      const recording = this.activePlayingCall();

      if (recording?.duration && recording.duration > 0) {
        // Always honour the pre-computed capped duration — never let the browser stream length overwrite it
        this.audioDuration.set(recording.duration);
      } else if (recording?.audioSegments?.length > 0) {
        const totalDuration = recording.audioSegments[recording.audioSegments.length - 1].endTimeInCall;
        this.audioDuration.set(totalDuration);
      } else if (isFinite(browserDuration) && browserDuration > 0) {
        const baseOffset = recording?.baseOffset || 0;
        if (baseOffset === 0) {
          this.audioDuration.set(browserDuration);
          this.activePlayingCall.update(c => c ? ({ ...c, duration: browserDuration }) : null);
        } else {
          const totalProbed = browserDuration + baseOffset;
          if (Math.abs(totalProbed - (this.audioDuration() || 0)) > 2) {
            this.audioDuration.set(totalProbed);
          }
        }
      }
    };

    this.audio.onplay = () => this.isPlaying.set(true);
    this.audio.onpause = () => this.isPlaying.set(false);
    this.audio.onended = () => {
      if (this._isChangingSource) return;

      const recording = this.activePlayingCall();
      if (recording && recording.audioSegments && recording.audioSegments.length > 0) {
        const nextIndex = this.currentSegmentIndex() + 1;
        if (nextIndex < recording.audioSegments.length) {
          const nextSegment = recording.audioSegments[nextIndex];
          this.playRecording(recording, nextSegment.startTimeInCall);
          return;
        }
      }

      this.isPlaying.set(false);
      this.onAudioEnded();
    };
    this.audio.onerror = () => {
      console.error('Audio error:', this.audio.error);
      this.isPlaying.set(false);
    };
  }

  private updateActiveTranscriptIndex(time: number): void {
    const transcript = this.activePlayingCall()?.formattedTranscript || [];
    let activeIndex = -1;
    for (let i = 0; i < transcript.length; i++) {
      const startTs = transcript[i].timestamp / 1000;
      const nextTs = transcript[i + 1] ? (transcript[i + 1].timestamp / 1000) : Infinity;
      if (time >= startTs && time < nextTs) {
        activeIndex = i;
        break;
      }
    }

    if (activeIndex !== -1 && activeIndex !== this.activeTranscriptIndex()) {
      this.activeTranscriptIndex.set(activeIndex);
      if (this.isAutoScrolling()) {
        this.scrollToActiveTranscript(activeIndex);
      } else {
        this.updateScrollDirection();
      }
    }
  }

  fetchCallDetails(): void {
    // Stricter Lock: Prevent simultaneous calls
    if (this.isLoading() || this.isLoadingHistory() || !this.hasMoreHistory()) return;

    const isFirstLoad = this.historyPage === 1;
    const currentTab = this.activeTab();

    if (isFirstLoad) {
      this.isLoading.set(true);
    } else {
      this.isLoadingHistory.set(true);
    }

    const params = {
      customerId: this._data.customerDetails.id,
      agentId: this._data.agentDetails.id,
      type: currentTab,
      page: this.historyPage,
      limit: this.HISTORY_LIMIT
    };

    // Cancel previous request if any
    this._fetchSubscription?.unsubscribe();

    this._fetchSubscription = this._apiService.get('call-history/details', params).subscribe({
      next: (res: any) => {
        // Active Tab Validation: Ignore response if tab has changed
        if (currentTab !== this.activeTab()) return;

        const history = res?.history || [];
        this.hasMoreHistory.set(res?.hasMoreHistory || false);

        if (isFirstLoad) {
          this.messages.set(history);
          this.isLoading.set(false);
          
          // Temporal Lock: Buffer during initial settle
          this.isLoadingHistory.set(true);
          setTimeout(() => {
            this.scrollToBottom();
            setTimeout(() => this.isLoadingHistory.set(false), 300);
          }, 100);
        } else {
          const scrollContainer = this.scrollArea.nativeElement;
          const oldScrollHeight = scrollContainer.scrollHeight;
          const oldScrollTop = scrollContainer.scrollTop;

          this.messages.update(prev => [...history, ...prev]);
          this.isLoadingHistory.set(false);
          
          // Maintain scroll position after prepending
          setTimeout(() => {
            const newScrollHeight = scrollContainer.scrollHeight;
            scrollContainer.scrollTop = oldScrollTop + (newScrollHeight - oldScrollHeight);
          }, 0);
        }
        this.historyPage++;
      },
      error: (err: any) => {
        if (currentTab === this.activeTab()) {
          this.isLoading.set(false);
          this.isLoadingHistory.set(false);
        }
        console.error(err);
      }
    });
  }

  handleScroll(event: Event): void {
    const target = event.target as HTMLElement;
    // Trigger when scrolled to top (using a small threshold for robustness)
    if (target.scrollTop <= 5 && this.hasMoreHistory() && !this.isLoadingHistory() && !this.isLoading()) {
      this.fetchCallDetails();
    }
  }

  scrollToBottom(): void {
    if (this.scrollArea?.nativeElement) {
      this.scrollArea.nativeElement.scrollTop = this.scrollArea.nativeElement.scrollHeight;
    }
  }

  isNewDay(index: number): boolean {
    if (index === 0) return true;
    const currentMsg = this.chatMessages()[index];
    const prevMsg = this.chatMessages()[index - 1];
    if (!currentMsg.timestamp || !prevMsg.timestamp) return false;

    const currentDate = new Date(currentMsg.timestamp).setHours(0, 0, 0, 0);
    const prevDate = new Date(prevMsg.timestamp).setHours(0, 0, 0, 0);
    return currentDate !== prevDate;
  }

  getDateLabel(timestamp: number | string | undefined): string {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.setHours(0, 0, 0, 0) === today.setHours(0, 0, 0, 0)) {
      return 'Today';
    } else if (date.setHours(0, 0, 0, 0) === yesterday.setHours(0, 0, 0, 0)) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString();
    }
  }

  onTabChange(tab: string): void {
    this._fetchSubscription?.unsubscribe();
    this.activeTab.set(tab);
    this.messages.set([]);
    this.historyPage = 1;
    this.hasMoreHistory.set(true);
    this.openSessionIndex.set(null); // Reset accordions
    this.fetchCallDetails();
  }

  toggleSession(index: number, element: HTMLElement): void {
    const isOpening = this.openSessionIndex() !== index;
    this.openSessionIndex.set(isOpening ? index : null);

    if (isOpening) {
      setTimeout(() => {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  }

  ngOnDestroy(): void {
    this._fetchSubscription?.unsubscribe();
    this.closePlayer();
  }

  closeDialog(): void {
    this._location.back();
  }

}
